import { cn } from "@/lib/utils";

type Background = "paper" | "surface" | "graph-band" | "ink" | "secondary";

const BG_CLASS: Record<Background, string> = {
  paper: "bg-background",
  surface: "bg-surface",
  "graph-band": "bg-graph-band",
  ink: "bg-ink",
  secondary: "bg-secondary",
};

/**
 * Consistent section wrapper with max-width, padding, and background.
 */
export function SectionShell({
  id,
  "aria-labelledby": ariaLabelledby,
  "aria-label": ariaLabel,
  bg = "paper",
  maxWidth = "5xl",
  padding = "default",
  border,
  className,
  innerClassName,
  children,
  "data-scrolly-root": dataScrollyRoot,
}: {
  id?: string;
  "aria-labelledby"?: string;
  "aria-label"?: string;
  bg?: Background;
  maxWidth?: "3xl" | "5xl" | "6xl" | "7xl" | "wide";
  padding?: "tight" | "compact" | "default" | "spacious" | "hero";
  border?: "top" | "bottom" | "both";
  className?: string;
  innerClassName?: string;
  children: React.ReactNode;
  "data-scrolly-root"?: boolean;
}) {
  const paddingClass = {
    tight: "py-9",
    compact: "py-10 sm:py-14",
    default: "py-16 sm:py-20",
    spacious: "py-20 sm:py-24",
    hero: "py-28 sm:py-32",
  }[padding];

  const borderMap: Record<string, string> = {
    top: "border-t border-border-subtle",
    bottom: "border-b border-border-subtle",
    both: "border-y border-border-subtle",
  };
  const borderClass = border ? borderMap[border] : "";

  const maxWidthClass = {
    "3xl": "max-w-3xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    "wide": "max-w-[1600px]",
  }[maxWidth];

  return (
    <section
      id={id}
      aria-labelledby={ariaLabelledby}
      aria-label={ariaLabel}
      className={cn(BG_CLASS[bg], paddingClass, borderClass, className)}
      data-scrolly-root={dataScrollyRoot ? "" : undefined}
    >
      <div className={cn(maxWidthClass, "mx-auto px-6", innerClassName)}>
        {children}
      </div>
    </section>
  );
}
